import React from 'react'
import { Alert, Button, Spin, Tag, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { horsesApi } from 'api/horses'
import { HorseForm } from 'components/horses/HorseForm/HorseForm'
import { PageWrapper } from 'components/ui'
import { EHorseStatus, HORSE_EXPIRED_REASON, IPopulatedHorseWithoutCount } from 'models/horse'
import { getHorseStatusView, getRepublishMessage } from 'shared/constants/horses'
import { adminRoutes } from 'shared/constants/routes'
import { getHorseDaysLeft } from 'shared/utils/getHorseDaysLeft'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'

export const MyHorse: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [horse, setHorse] = React.useState<IPopulatedHorseWithoutCount | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isRepublishing, setIsRepublishing] = React.useState(false)

  const getHorse = async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const response = await horsesApi.getHorseForManage(id)
      setHorse(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getHorse()
  }, [id])

  const republish = async () => {
    if (!id) return
    try {
      setIsRepublishing(true)
      const { data } = await horsesApi.republishHorse(id)
      showMessage('success', getRepublishMessage(data.status), '')
      getHorse()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsRepublishing(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size='large' />
      </div>
    )
  }

  if (!horse) return null

  const daysLeft = getHorseDaysLeft(horse.published_until)
  const statusView = getHorseStatusView(horse)

  return (
    <PageWrapper>
      <Typography.Link
        onClick={() => navigate(adminRoutes.HORSES)}
        style={{ display: 'inline-block', marginBottom: 8 }}
      >
        <ArrowLeftOutlined /> Назад к моим лошадям
      </Typography.Link>
      <Typography.Title level={4}>
        №{horse.number} {horse.name}
      </Typography.Title>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Tag color={statusView.color}>{statusView.label}</Tag>
        {horse.status === EHorseStatus.PUBLISHED && daysLeft !== null && (
          <Tag color={daysLeft > 7 ? 'blue' : 'orange'}>
            Осталось дней: {daysLeft}
          </Tag>
        )}
      </div>

      {horse.reason_of_cancel && (
        <Alert
          type='error'
          showIcon
          message='Заявка отклонена'
          description={horse.reason_of_cancel}
          style={{ marginBottom: 16 }}
        />
      )}

      {horse.status === EHorseStatus.ARCHIVED && (
        <Alert
          type='info'
          showIcon
          message={statusView.note ? `${statusView.label} — ${statusView.note}` : statusView.label}
          description={
            horse.archived_reason === HORSE_EXPIRED_REASON
              ? 'Если объявление не меняли после проверки, оно сразу вернётся в каталог на 30 дней, иначе уйдёт на модерацию.'
              : 'Разместить заново можно только через модерацию.'
          }
          action={
            <Button type='primary' loading={isRepublishing} onClick={republish}>
              Разместить заново
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <HorseForm horse={horse} onSaved={getHorse} />
    </PageWrapper>
  )
}
